import {WhatIfScenarioConfig, WhatIfScenarioInputValues, WhatIfScenarioMappingConfig} from "../../ps-types";
import {ValueType} from "../../ps-models/line-items";
import {assocPath} from "ramda";
import {Grid, Input} from "semantic-ui-react";
import {convertToNumberStringOrBoolean, entries} from "../../ps-models";
import React, {useCallback, useEffect, useState} from "react";
import { TableLocatorInput } from "./TableLocatorInput";
import { ClosableSection } from "../../ClosableSection";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
// import { TableLocatorInput } from "./TableLocatorInput";

export function LocatorsMapInput({mappingConfig, inputValues, onValuesUpdated, compact = false, sectioningByWorksheet, useDatePickersForDateInputs}: {
  mappingConfig: WhatIfScenarioConfig['mappingConfig'],
  inputValues: WhatIfScenarioInputValues,
  onValuesUpdated: (values: WhatIfScenarioInputValues) => void,
  compact?: boolean,
  sectioningByWorksheet?: {
    customMapping?: Record<string, string[]>;
  },
  useDatePickersForDateInputs?: boolean
}) {

  const [values, setValues] = useState<Record<string, string[][]>>({});

  // Helper function to parse MMDDYYYY format to Date
  const parseDateFromMMDDYYYY = (dateStr: string | undefined): Date | null => {
    if (!dateStr || typeof dateStr !== 'string') return null;
    // Remove any non-digit characters and ensure we have exactly 8 digits
    const digits = dateStr.replace(/\D/g, '');
    if (digits.length !== 8) return null;
    
    const month = parseInt(digits.substring(0, 2), 10) - 1; // Month is 0-indexed
    const day = parseInt(digits.substring(2, 4), 10);
    const year = parseInt(digits.substring(4, 8), 10);
    
    const date = new Date(year, month, day);
    if (isNaN(date.getTime())) return null;
    return date;
  };

    // Helper function to parse MMDDYYYY format to Date
    const parseDateFromMMYYYY = (dateStr: string | undefined): Date | null => {
      if (!dateStr || typeof dateStr !== 'string') return null;
      // Remove any non-digit characters and ensure we have exactly 8 digits
      const digits = dateStr.replace(/\D/g, '');
      if (digits.length !== 6) return null;
      
      const month = parseInt(digits.substring(0, 2), 10) - 1; // Month is 0-indexed
      const year = parseInt(digits.substring(2, 6), 10);
      
      const date = new Date(year, month, 1);
      if (isNaN(date.getTime())) return null;
      return date;
    };

  // Helper function to format Date to MM/DD/YYYY format
  const formatDateToMMDDYYYY = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString();
    return `${month}/${day}/${year}`;
  };

  useEffect(() => {
    entries(inputValues).forEach(([key, value]) => {
      let mappingConfigLocatorType = mappingConfig[key]?.locator?.type;
      setValues((prev)=> {
        let valueToSet;
        if(mappingConfigLocatorType === "table") {
          valueToSet = value.value.map((r)=>r?.map((v)=>v?.toString()));
        } else {
          valueToSet = [[value?.value?.[0]?.[0]?.toString()]];
        }
        return {
          ...prev,
          [key]: valueToSet
        }
      })
    });
  }, [inputValues]);

  const handleLocatorChange = useCallback((valueKey: string, value: ValueType) => {

    //Update the corresponding value
    let valuesUpdated = assocPath([valueKey],
      ({value: [[ convertToNumberStringOrBoolean(value)]], valueKey: valueKey}),
      inputValues
    );

    onValuesUpdated(valuesUpdated);
  }, [inputValues, onValuesUpdated])

  const handleTableLocatorChange = useCallback((valueKey: string, value: ValueType[][]) => {

    //Update the corresponding value
    let valuesUpdated = assocPath([valueKey],
      ({value: value.map((r)=>r.map((c)=>convertToNumberStringOrBoolean(c))), valueKey: valueKey}),
      inputValues
    );

    onValuesUpdated(valuesUpdated);
  }, [inputValues, onValuesUpdated])

  const getResultantGrid = useCallback((configs: WhatIfScenarioMappingConfig[]) => {
        return <Grid stackable={compact} style={{marginBottom: "10px"}}>
        {configs.map((conf) => {
          let key = conf.valueKey;
          const isDateField = useDatePickersForDateInputs && (conf.label?.endsWith("(mm/dd/yyyy)") || conf.label?.endsWith("(mm/yyyy)"));
          if(compact){
            return <Grid.Column width={4} key={key}>
            <Grid.Column>
              <strong>{conf.label}</strong>
            </Grid.Column>
            <Grid.Column>
              {conf.locator.type === "table" && <>
                <TableLocatorInput
                  data={values[key]}
                  onChange={(newData: string[][]) => {
                    setValues({...values, [key]: newData})
                  }}
                  onTableBlur={(data) => {
                    handleTableLocatorChange(key, data)
                  }}
                  />
              </>}
              {(conf.locator.type === "cell" || conf.locator.type === "parameter") && isDateField && (
                <DatePicker
                  selected={parseDateFromMMDDYYYY(values?.[key]?.[0]?.[0] || "")}
                  onChange={(date: Date | null) => {
                    const formattedDate = date ? formatDateToMMDDYYYY(date) : "";
                    setValues({...values, [key]: [[formattedDate]]});
                  }}
                  onBlur={(e:any) => {
                    let dateString = e.target.value;
                    const date = parseDateFromMMYYYY(dateString);
                    const formattedDate = date ? formatDateToMMDDYYYY(date) : "";
                    handleLocatorChange(key, formattedDate);
                  }}
                  showMonthYearPicker
                  dateFormat="MM/yyyy"
                  placeholderText="Select month/year"
                />
              )}
              {(conf.locator.type === "cell" || conf.locator.type === "parameter") && !isDateField && <Input
                      value={values?.[key]?.[0]?.[0] || ""}
                      onChange={(e:any) => {
                        setValues({...values, [key]: [[e.target.value]]})
                      }}
                     onBlur={(e:any) => {
                       handleLocatorChange(key, e.target.value)
                     }}
              />}

            </Grid.Column>
          </Grid.Column>
          }
          return <Grid.Row columns={2} key={key}>
            <Grid.Column>
              <strong>{conf.label}</strong>
            </Grid.Column>
            <Grid.Column>
              {conf.locator.type === "table" && <>
                <TableLocatorInput
                  data={values[key]}
                  onChange={(newData: string[][]) => {
                    setValues({...values, [key]: newData})
                  }}
                  onTableBlur={(data) => {
                    handleTableLocatorChange(key, data)
                  }}
                  />
              </>}
              {(conf.locator.type === "cell" || conf.locator.type === "parameter") && isDateField && (
                <DatePicker
                selected={parseDateFromMMDDYYYY(values?.[key]?.[0]?.[0] || "")}
                onChange={(date: Date | null) => {
                  const formattedDate = date ? formatDateToMMDDYYYY(date) : "";
                  setValues({...values, [key]: [[formattedDate]]});
                }}
                onBlur={(e:any) => {
                  let dateString = e.target.value;
                  const date = parseDateFromMMYYYY(dateString);
                  const formattedDate = date ? formatDateToMMDDYYYY(date) : "";
                  handleLocatorChange(key, formattedDate);
                }}
                showMonthYearPicker
                dateFormat="MM/yyyy"
                placeholderText="Select month/year"
              />
              )}
              {(conf.locator.type === "cell" || conf.locator.type === "parameter") && !isDateField && <Input
                      value={values?.[key]?.[0]?.[0] || ""}
                      onChange={(e:any) => {
                        setValues({...values, [key]: [[e.target.value]]})
                      }}
                     onBlur={(e:any) => {
                       handleLocatorChange(key, e.target.value)
                     }}
              />}
            </Grid.Column>
          </Grid.Row>
        })
        }
        </Grid>
  }, [compact, values, setValues, handleLocatorChange, handleTableLocatorChange, useDatePickersForDateInputs])
  

  if(sectioningByWorksheet) {
      // Group configs by worksheet
      const configsGroupedByWorksheet = Object.values(mappingConfig).reduce((acc, conf) => {
        let worksheetName = (Object.entries(sectioningByWorksheet?.customMapping ?? {}).find(([key, value]) => value.includes(conf.label))?.[0] ?? conf.locator?.workSheet) || "General";
        if (!acc[worksheetName]) {
          acc[worksheetName] = [];
        }
        acc[worksheetName].push(conf);
        return acc;
      }, {} as Record<string, typeof mappingConfig[string][]>);
    return <>
      {Object.entries(configsGroupedByWorksheet).map(([worksheetName, configs]) => {
      return <ClosableSection key={worksheetName} title={worksheetName} opened={true}>
      {getResultantGrid(configs)}
      </ClosableSection>
    })}
    </>
  } else {
    return getResultantGrid(Object.values(mappingConfig))
  }
}